import React from 'react';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/material.css';
import TextInputComponent from '../../../../components/input/TextInputComponent';
import { Divider, SaveButton, SkillkoFieldBadge, EditedText } from '../components';
import { shouldShowEdited, isSkillkoSourcedEdit } from '../profileFormConfig';

/**
 * Emergency Contact section of the profile form
 * Handles: nextOfKin, nextOfKinPhone
 */
const EmergencyContactSection = ({
  mode,
  config,
  profileData,
  initialProfileData,
  handleInputChange,
  checkFieldDisabled,
  onSave,
  isEdited,
  canEdit,
}) => {
  const { sectionSave } = config;
  const showEditIndicator = shouldShowEdited(mode, 'nextOfKin');
  const isSkillkoEdit = isSkillkoSourcedEdit(mode, profileData);
  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const gridSpacing = isSmUp ? 3 : 2;

  // Generate stable keys - only changes when disabled state changes
  const getFieldKey = (fieldName, disabled) => {
    return `${fieldName}-${!!disabled}`;
  };

  return (
    <>
      <Grid item xs={12} marginBottom={{ xs: 2, sm: 3 }} marginTop={{ xs: 2, sm: 3 }}>
        <Divider text="Emergency Contact" />
      </Grid>

      <Grid container spacing={gridSpacing}>
        {/* Emergency Contact Name */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <TextInputComponent
                    key={getFieldKey('nextOfKin', checkFieldDisabled('nextOfKin'))}
                    value={profileData.nextOfKin}
                    label="Emergency Contact"
                    inputLabelProps={{ shrink: true }}
                    setValue={(value) => handleInputChange('nextOfKin', value)}
                    disabled={checkFieldDisabled('nextOfKin')}
                  />
                </Box>
                {isSkillkoEdit && (
                  <Box sx={{ mt: 2 }}>
                    <SkillkoFieldBadge />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item sm={6} xs={3}>
              {initialProfileData.nextOfKin !== profileData.nextOfKin && (
                <EditedText />
              )}
            </Grid>
          </>
        ) : (
          <>
            <Grid item sm={8} xs={12}>
              <TextInputComponent
                key={getFieldKey('nextOfKin', checkFieldDisabled('nextOfKin'))}
                value={profileData.nextOfKin}
                label="Emergency Contact"
                inputLabelProps={{ shrink: true }}
                setValue={(value) => handleInputChange('nextOfKin', value)}
                disabled={checkFieldDisabled('nextOfKin')}
              />
            </Grid>
            <Grid item sm={4} xs={0}></Grid>
          </>
        )}

        {/* Emergency Contact Phone */}
        {sectionSave ? (
          <>
            <Grid item sm={6} xs={9}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <PhoneInput
                    key={getFieldKey('nextOfKinPhone', checkFieldDisabled('nextOfKinPhone'))}
                    specialLabel="Emergency Contact Number"
                    country="ie"
                    value={profileData.nextOfKinPhone}
                    onChange={(value) => handleInputChange('nextOfKinPhone', value)}
                    disabled={checkFieldDisabled('nextOfKinPhone')}
                    inputStyle={{ width: '100%' }}
                  />
                </Box>
                {isSkillkoEdit && (
                  <Box sx={{ mt: 2 }}>
                    <SkillkoFieldBadge />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item container sm={6} xs={3} justifyContent="space-between">
              <Grid item>
                {initialProfileData.nextOfKinPhone !== profileData.nextOfKinPhone && (
                  <EditedText />
                )}
              </Grid>
              <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
            <Grid 
              item 
              container 
              xs={12} 
              justifyContent={{ xs: 'stretch', sm: 'flex-end' }}
              sx={{ mt: { xs: 1, sm: 0 } }}
            >
              <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                <SaveButton 
                  disabled={!isEdited} 
                  onClick={onSave}
                  sectionSave={sectionSave}
                  canEdit={canEdit}
                />
              </Box>
            </Grid>
          </>
        ) : (
          <Grid item sm={8} xs={12}>
            <PhoneInput
              key={getFieldKey('nextOfKinPhone', checkFieldDisabled('nextOfKinPhone'))}
              specialLabel="Emergency Contact Number"
              country="ie"
              value={profileData.nextOfKinPhone || ''}
              onChange={(value) => handleInputChange('nextOfKinPhone', value)}
              disabled={checkFieldDisabled('nextOfKinPhone')}
              inputStyle={{ width: '100%' }}
              containerStyle={checkFieldDisabled('nextOfKinPhone') ? {
                opacity: '0.38',
                border: '1px solid rgba(0, 0, 0, 0.87)',
                borderRadius: '8px',
              } : undefined}
            />
          </Grid>
        )}
      </Grid>
    </>
  );
};

export default EmergencyContactSection;

